import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Button,
    Paper,
    FormControl,
    Select,
    MenuItem,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SubtitlesIcon from '@mui/icons-material/Subtitles';

const SubtitleEditor = ({ subtitleData, onSubtitlesChange, onSettingsChange, projectId }) => {
    const [subtitles, setSubtitles] = useState(subtitleData.subtitles || []);
    const [settings, setSettings] = useState(subtitleData.settings || {});
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingSubtitle, setEditingSubtitle] = useState(null);
    const [expandSubtitles, setExpandSubtitles] = useState(true);
    const [isAddingNewSubtitle, setIsAddingNewSubtitle] = useState(false);
    const [newSubtitle, setNewSubtitle] = useState({
        start: 0,
        end: 5,
        text: '',
        speaker: 'SPEAKER_0'
    });
    const [speakers, setSpeakers] = useState([]);
    const [saveTimeout, setSaveTimeout] = useState(null);
    const [loading, setLoading] = useState(false);
    const [videoTotalDuration, setVideoTotalDuration] = useState(300); // Default 5 minutes (300 seconds)

    // Style constants matching the SubtitleManager
    const PRIMARY_COLOR = '#6366f1';
    const SECONDARY_COLOR = '#4f46e5';
    const PANEL_BG = '#212224';
    const BACKGROUND_COLOR = '#2D2D2D';
    const ITEM_BG = 'rgba(32, 33, 36, 0.9)';

    // Extract unique speakers from subtitles on component mount
    useEffect(() => {
        if (subtitleData && subtitleData.subtitles) {
            setSubtitles(subtitleData.subtitles);

            // Extract unique speakers
            const uniqueSpeakers = [...new Set(subtitleData.subtitles.map(sub => sub.speaker))];
            setSpeakers(uniqueSpeakers);
        }

        if (subtitleData && subtitleData.settings) {
            setSettings(subtitleData.settings);
        }
    }, [subtitleData]);

    // Format time from seconds to mm:ss.SSS
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = (seconds % 60).toFixed(3);
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.padStart(6, '0')}`;
    };

    // Parse time from mm:ss.SSS to seconds
    const parseTime = (timeString) => {
        try {
            // Handle various input formats
            let minutes = 0;
            let seconds = 0;

            if (timeString.includes(':')) {
                const [mins, secs] = timeString.split(':');
                minutes = parseInt(mins) || 0;
                seconds = parseFloat(secs) || 0;
            } else {
                // If only a number is entered, treat as seconds
                seconds = parseFloat(timeString) || 0;
            }

            return minutes * 60 + seconds;
        } catch (error) {
            console.error('Error parsing time:', error);
            return 0;
        }
    };

    // Handle edit button click
    const handleEditClick = (e, index, item) => {
        e.stopPropagation();
        setEditingIndex(index);
        setEditingSubtitle({ ...item });
    };

    // Handle delete button click
    const handleDeleteClick = (e, index) => {
        e.stopPropagation();
        const newSubtitles = [...subtitles];
        newSubtitles.splice(index, 1);
        setSubtitles(newSubtitles);

        // Notify parent of changes
        if (onSubtitlesChange) {
            onSubtitlesChange(newSubtitles);
        }
    };

    // Handle save changes
    const handleSaveClick = () => {
        if (editingSubtitle) {
            const newSubtitles = [...subtitles];
            newSubtitles[editingIndex] = editingSubtitle;

            // Sort subtitles by start time
            newSubtitles.sort((a, b) => a.start - b.start);

            setSubtitles(newSubtitles);

            // Notify parent of changes
            if (onSubtitlesChange) {
                onSubtitlesChange(newSubtitles);
            }

            setEditingIndex(null);
            setEditingSubtitle(null);
        }
    };

    // Handle cancel editing
    const handleCancelClick = () => {
        setEditingIndex(null);
        setEditingSubtitle(null);
    };

    // Handle field changes during editing
    const handleEditChange = (field, value) => {
        if (!editingSubtitle) return;

        const updatedSubtitle = { ...editingSubtitle };

        if (field === 'start' || field === 'end') {
            // If it's a valid number, update
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue >= 0) {
                updatedSubtitle[field] = numValue;

                // Ensure end time is always after start time
                if (field === 'start' && updatedSubtitle.start >= updatedSubtitle.end) {
                    updatedSubtitle.end = updatedSubtitle.start + 0.1;
                }
            }
        } else {
            updatedSubtitle[field] = value;
        }

        setEditingSubtitle(updatedSubtitle);
    };

    // Handle opening add form
    const handleAddSubtitleClick = () => {
        // Initialize new subtitle with end time after last subtitle
        // or start at 0 if no subtitles exist
        if (subtitles.length > 0) {
            const lastSubtitle = subtitles[subtitles.length - 1];
            setNewSubtitle({
                start: lastSubtitle.end,
                end: lastSubtitle.end + 5, // Default 5 seconds duration
                text: '',
                speaker: speakers.length > 0 ? speakers[0] : 'SPEAKER_0'
            });
        }
        setIsAddingNewSubtitle(true);
    };

    // Handle adding new subtitle
    const handleAddSubtitle = () => {
        if (!newSubtitle.text) return;

        const updatedSubtitles = [...subtitles, newSubtitle];

        // Sort subtitles by start time
        updatedSubtitles.sort((a, b) => a.start - b.start);

        setSubtitles(updatedSubtitles);

        // Notify parent of changes
        if (onSubtitlesChange) {
            onSubtitlesChange(updatedSubtitles);
        }

        setIsAddingNewSubtitle(false);

        // Reset form
        setNewSubtitle({
            start: 0,
            end: 5,
            text: '',
            speaker: speakers.length > 0 ? speakers[0] : 'SPEAKER_0'
        });
    };

    // Handle canceling new subtitle
    const handleCancelNewSubtitle = () => {
        setIsAddingNewSubtitle(false);
    };

    // Handle new subtitle form changes
    const handleNewSubtitleChange = (field, value) => {
        const updatedNewSubtitle = { ...newSubtitle };

        if (field === 'start' || field === 'end') {
            // If it's a valid number, update
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue >= 0) {
                updatedNewSubtitle[field] = numValue;

                // Ensure end time is always after start time
                if (field === 'start' && updatedNewSubtitle.start >= updatedNewSubtitle.end) {
                    updatedNewSubtitle.end = updatedNewSubtitle.start + 0.1;
                }
            }
        } else {
            updatedNewSubtitle[field] = value;
        }

        setNewSubtitle(updatedNewSubtitle);
    };

    // Style for panel headers
    const panelHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1.5,
        borderBottom: expandSubtitles ? '1px solid rgba(255, 255, 255, 0.07)' : 'none',
        cursor: 'pointer',
    };

    // Style for panel content
    const panelContentStyle = {
        py: 1,
        px: 1.5,
    };



    return (
        <Paper elevation={0} sx={{
            backgroundColor: PANEL_BG,
            borderRadius: '4px',
            padding: '0px',
            mb: 1,
            mt: 2
        }}>
            <Box
                sx={panelHeaderStyle}
                onClick={() => setExpandSubtitles(!expandSubtitles)}
            >
                <Typography sx={{
                    color: 'white',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    <SubtitlesIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'text-top' }} />
                    Subtitles
                </Typography>
                {expandSubtitles ?
                    <ExpandLessIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem' }} /> :
                    <ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem' }} />
                }
            </Box>

            {expandSubtitles && (
                <Box sx={panelContentStyle}>
                    {/* Button to add new subtitle */}
                    {!isAddingNewSubtitle && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddSubtitleClick}
                                sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    borderColor: 'rgba(255,255,255,0.23)',
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    '&:hover': {
                                        borderColor: PRIMARY_COLOR,
                                        color: PRIMARY_COLOR,
                                        backgroundColor: 'rgba(255,255,255,0.05)'
                                    }
                                }}
                            >
                                Add Subtitle
                            </Button>
                        </Box>
                    )}

                    {/* Form to add new subtitle */}
                    {isAddingNewSubtitle && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: '4px',
                                backgroundColor: ITEM_BG,
                                border: '1px solid rgba(255,255,255,0.1)'
                            }}
                        >
                            <Typography sx={{
                                color: 'white',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                mb: 1.5
                            }}>
                                New Subtitle
                            </Typography>

                            <Box sx={{ mb: 1.5 }}>
                                <TextField
                                    fullWidth
                                    label="Subtitle Text"
                                    value={newSubtitle.text}
                                    onChange={(e) => handleNewSubtitleChange('text', e.target.value)}
                                    multiline
                                    rows={2}
                                    size="small"
                                    sx={{
                                        mb: 1.5,
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                            '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                            '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                        },
                                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY_COLOR }
                                    }}
                                />

                                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', mb: 0.5 }}>
                                        Start Time
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TextField
                                            value={Math.floor(newSubtitle.start / 60)}
                                            onChange={(e) => {
                                                const mins = parseInt(e.target.value);
                                                if (!isNaN(mins) && mins >= 0) {
                                                    const secs = newSubtitle.start % 60;
                                                    const secsInt = Math.floor(secs);
                                                    const ms = Math.round((secs - secsInt) * 1000);
                                                    const newTime = mins * 60 + secsInt + (ms / 1000);
                                                    handleNewSubtitleChange('start', newTime);
                                                }
                                            }}
                                            size="small"
                                            variant="outlined"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: { textAlign: 'center' }
                                            }}
                                            placeholder="min"
                                            sx={{
                                                width: '60px',
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                }
                                            }}
                                        />
                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>:</Typography>
                                        <TextField
                                            value={Math.floor(newSubtitle.start % 60)}
                                            onChange={(e) => {
                                                const secs = parseInt(e.target.value);
                                                if (!isNaN(secs) && secs >= 0 && secs < 60) {
                                                    const mins = Math.floor(newSubtitle.start / 60);
                                                    const ms = Math.round(((newSubtitle.start % 60) - Math.floor(newSubtitle.start % 60)) * 1000);
                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                    handleNewSubtitleChange('start', newTime);
                                                }
                                            }}
                                            size="small"
                                            variant="outlined"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: { textAlign: 'center' }
                                            }}
                                            placeholder="sec"
                                            sx={{
                                                width: '60px',
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                }
                                            }}
                                        />
                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>.</Typography>
                                        <TextField
                                            value={Math.round(((newSubtitle.start % 60) - Math.floor(newSubtitle.start % 60)) * 1000)}
                                            onChange={(e) => {
                                                const ms = parseInt(e.target.value);
                                                if (!isNaN(ms) && ms >= 0 && ms < 1000) {
                                                    const mins = Math.floor(newSubtitle.start / 60);
                                                    const secs = Math.floor(newSubtitle.start % 60);
                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                    handleNewSubtitleChange('start', newTime);
                                                }
                                            }}
                                            size="small"
                                            variant="outlined"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: { textAlign: 'center' }
                                            }}
                                            placeholder="ms"
                                            sx={{
                                                width: '70px',
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', mb: 0.5 }}>
                                        End Time
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TextField
                                            value={Math.floor(newSubtitle.end / 60)}
                                            onChange={(e) => {
                                                const mins = parseInt(e.target.value);
                                                if (!isNaN(mins) && mins >= 0) {
                                                    const secs = newSubtitle.end % 60;
                                                    const secsInt = Math.floor(secs);
                                                    const ms = Math.round((secs - secsInt) * 1000);
                                                    const newTime = mins * 60 + secsInt + (ms / 1000);
                                                    if (newTime > newSubtitle.start) {
                                                        handleNewSubtitleChange('end', newTime);
                                                    }
                                                }
                                            }}
                                            size="small"
                                            variant="outlined"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: { textAlign: 'center' }
                                            }}
                                            placeholder="min"
                                            sx={{
                                                width: '60px',
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                }
                                            }}
                                        />
                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>:</Typography>
                                        <TextField
                                            value={Math.floor(newSubtitle.end % 60)}
                                            onChange={(e) => {
                                                const secs = parseInt(e.target.value);
                                                if (!isNaN(secs) && secs >= 0 && secs < 60) {
                                                    const mins = Math.floor(newSubtitle.end / 60);
                                                    const ms = Math.round(((newSubtitle.end % 60) - Math.floor(newSubtitle.end % 60)) * 1000);
                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                    if (newTime > newSubtitle.start) {
                                                        handleNewSubtitleChange('end', newTime);
                                                    }
                                                }
                                            }}
                                            size="small"
                                            variant="outlined"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: { textAlign: 'center' }
                                            }}
                                            placeholder="sec"
                                            sx={{
                                                width: '60px',
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                }
                                            }}
                                        />
                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>.</Typography>
                                        <TextField
                                            value={Math.round(((newSubtitle.end % 60) - Math.floor(newSubtitle.end % 60)) * 1000)}
                                            onChange={(e) => {
                                                const ms = parseInt(e.target.value);
                                                if (!isNaN(ms) && ms >= 0 && ms < 1000) {
                                                    const mins = Math.floor(newSubtitle.end / 60);
                                                    const secs = Math.floor(newSubtitle.end % 60);
                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                    if (newTime > newSubtitle.start) {
                                                        handleNewSubtitleChange('end', newTime);
                                                    }
                                                }
                                            }}
                                            size="small"
                                            variant="outlined"
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: { textAlign: 'center' }
                                            }}
                                            placeholder="ms"
                                            sx={{
                                                width: '70px',
                                                '& .MuiOutlinedInput-root': {
                                                    color: 'white',
                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                    <Typography sx={{
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: '0.75rem',
                                        mb: 0.5
                                    }}>
                                        Speaker
                                    </Typography>
                                    <Select
                                        value={newSubtitle.speaker}
                                        onChange={(e) => handleNewSubtitleChange('speaker', e.target.value)}
                                        size="small"
                                        sx={{
                                            color: 'white',
                                            fontSize: '0.8rem',
                                            height: '36px',
                                            bgcolor: ITEM_BG,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255,255,255,0.23)',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: PRIMARY_COLOR,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: PRIMARY_COLOR,
                                            },
                                        }}
                                    >
                                        {speakers.map((speaker) => (
                                            <MenuItem key={speaker} value={speaker}>
                                                {speaker}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button
                                    size="small"
                                    onClick={handleCancelNewSubtitle}
                                    sx={{
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: '0.75rem',
                                        '&:hover': {
                                            color: PRIMARY_COLOR,
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleAddSubtitle}
                                    disabled={!newSubtitle.text || loading}
                                    sx={{
                                        bgcolor: PRIMARY_COLOR,
                                        fontSize: '0.75rem',
                                        '&:hover': {
                                            bgcolor: SECONDARY_COLOR,
                                        },
                                    }}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* List of subtitles */}
                    <Box
                        sx={{
                            maxHeight: '500px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#555',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#333',
                                borderRadius: '4px',
                            },
                        }}
                    >
                        {subtitles.map((subtitle, index) => (
                            <Box
                                key={index}
                                sx={{
                                    mb: 1,
                                    backgroundColor: ITEM_BG,
                                    borderRadius: '4px',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    '&:hover': {
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                    },
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1.5,
                                }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            <Typography variant="caption" sx={{
                                                color: 'rgba(255,255,255,0.7)',
                                                fontWeight: 'bold',
                                                mr: 1,
                                            }}>
                                                {formatTime(subtitle.start)} - {formatTime(subtitle.end)}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: PRIMARY_COLOR,
                                                    color: 'white',
                                                    borderRadius: '2px',
                                                    px: 0.5,
                                                    py: 0.25,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {subtitle.speaker}
                                            </Box>
                                        </Box>

                                        {editingIndex === index ? (
                                            <Box>
                                                <TextField
                                                    value={editingSubtitle.text}
                                                    onChange={(e) => handleEditChange('text', e.target.value)}
                                                    multiline
                                                    fullWidth
                                                    size="small"
                                                    sx={{
                                                        mb: 1.5,
                                                        '& .MuiOutlinedInput-root': {
                                                            color: 'white',
                                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                            '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                            '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                        }
                                                    }}
                                                />

                                                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', mb: 0.5 }}>
                                                        Start Time
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <TextField
                                                            value={Math.floor(editingSubtitle.start / 60)}
                                                            onChange={(e) => {
                                                                const mins = parseInt(e.target.value);
                                                                if (!isNaN(mins) && mins >= 0) {
                                                                    const secs = editingSubtitle.start % 60;
                                                                    const secsInt = Math.floor(secs);
                                                                    const ms = Math.round((secs - secsInt) * 1000);
                                                                    const newTime = mins * 60 + secsInt + (ms / 1000);
                                                                    handleEditChange('start', newTime);
                                                                }
                                                            }}
                                                            size="small"
                                                            variant="outlined"
                                                            inputProps={{
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                style: { textAlign: 'center' }
                                                            }}
                                                            placeholder="min"
                                                            sx={{
                                                                width: '60px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    color: 'white',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                                }
                                                            }}
                                                        />
                                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>:</Typography>
                                                        <TextField
                                                            value={Math.floor(editingSubtitle.start % 60)}
                                                            onChange={(e) => {
                                                                const secs = parseInt(e.target.value);
                                                                if (!isNaN(secs) && secs >= 0 && secs < 60) {
                                                                    const mins = Math.floor(editingSubtitle.start / 60);
                                                                    const ms = Math.round(((editingSubtitle.start % 60) - Math.floor(editingSubtitle.start % 60)) * 1000);
                                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                                    handleEditChange('start', newTime);
                                                                }
                                                            }}
                                                            size="small"
                                                            variant="outlined"
                                                            inputProps={{
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                style: { textAlign: 'center' }
                                                            }}
                                                            placeholder="sec"
                                                            sx={{
                                                                width: '60px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    color: 'white',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                                }
                                                            }}
                                                        />
                                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>.</Typography>
                                                        <TextField
                                                            value={Math.round(((editingSubtitle.start % 60) - Math.floor(editingSubtitle.start % 60)) * 1000)}
                                                            onChange={(e) => {
                                                                const ms = parseInt(e.target.value);
                                                                if (!isNaN(ms) && ms >= 0 && ms < 1000) {
                                                                    const mins = Math.floor(editingSubtitle.start / 60);
                                                                    const secs = Math.floor(editingSubtitle.start % 60);
                                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                                    handleEditChange('start', newTime);
                                                                }
                                                            }}
                                                            size="small"
                                                            variant="outlined"
                                                            inputProps={{
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                style: { textAlign: 'center' }
                                                            }}
                                                            placeholder="ms"
                                                            sx={{
                                                                width: '70px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    color: 'white',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', mb: 0.5 }}>
                                                        End Time
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <TextField
                                                            value={Math.floor(editingSubtitle.end / 60)}
                                                            onChange={(e) => {
                                                                const mins = parseInt(e.target.value);
                                                                if (!isNaN(mins) && mins >= 0) {
                                                                    const secs = editingSubtitle.end % 60;
                                                                    const secsInt = Math.floor(secs);
                                                                    const ms = Math.round((secs - secsInt) * 1000);
                                                                    const newTime = mins * 60 + secsInt + (ms / 1000);
                                                                    if (newTime > editingSubtitle.start) {
                                                                        handleEditChange('end', newTime);
                                                                    }
                                                                }
                                                            }}
                                                            size="small"
                                                            variant="outlined"
                                                            inputProps={{
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                style: { textAlign: 'center' }
                                                            }}
                                                            placeholder="min"
                                                            sx={{
                                                                width: '60px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    color: 'white',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                                }
                                                            }}
                                                        />
                                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>:</Typography>
                                                        <TextField
                                                            value={Math.floor(editingSubtitle.end % 60)}
                                                            onChange={(e) => {
                                                                const secs = parseInt(e.target.value);
                                                                if (!isNaN(secs) && secs >= 0 && secs < 60) {
                                                                    const mins = Math.floor(editingSubtitle.end / 60);
                                                                    const ms = Math.round(((editingSubtitle.end % 60) - Math.floor(editingSubtitle.end % 60)) * 1000);
                                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                                    if (newTime > editingSubtitle.start) {
                                                                        handleEditChange('end', newTime);
                                                                    }
                                                                }
                                                            }}
                                                            size="small"
                                                            variant="outlined"
                                                            inputProps={{
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                style: { textAlign: 'center' }
                                                            }}
                                                            placeholder="sec"
                                                            sx={{
                                                                width: '60px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    color: 'white',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                                }
                                                            }}
                                                        />
                                                        <Typography sx={{ mx: 1, color: 'rgba(255,255,255,0.7)' }}>.</Typography>
                                                        <TextField
                                                            value={Math.round(((editingSubtitle.end % 60) - Math.floor(editingSubtitle.end % 60)) * 1000)}
                                                            onChange={(e) => {
                                                                const ms = parseInt(e.target.value);
                                                                if (!isNaN(ms) && ms >= 0 && ms < 1000) {
                                                                    const mins = Math.floor(editingSubtitle.end / 60);
                                                                    const secs = Math.floor(editingSubtitle.end % 60);
                                                                    const newTime = mins * 60 + secs + (ms / 1000);
                                                                    if (newTime > editingSubtitle.start) {
                                                                        handleEditChange('end', newTime);
                                                                    }
                                                                }
                                                            }}
                                                            size="small"
                                                            variant="outlined"
                                                            inputProps={{
                                                                inputMode: 'numeric',
                                                                pattern: '[0-9]*',
                                                                style: { textAlign: 'center' }
                                                            }}
                                                            placeholder="ms"
                                                            sx={{
                                                                width: '70px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    color: 'white',
                                                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.23)' },
                                                                    '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                                                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                                    <Typography sx={{
                                                        color: 'rgba(255,255,255,0.7)',
                                                        fontSize: '0.75rem',
                                                        mb: 0.5
                                                    }}>
                                                        Speaker
                                                    </Typography>
                                                    <Select
                                                        value={editingSubtitle.speaker}
                                                        onChange={(e) => handleEditChange('speaker', e.target.value)}
                                                        size="small"
                                                        sx={{
                                                            color: 'white',
                                                            fontSize: '0.8rem',
                                                            height: '36px',
                                                            bgcolor: 'rgba(32, 33, 36, 0.7)',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255,255,255,0.23)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: PRIMARY_COLOR,
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: PRIMARY_COLOR,
                                                            },
                                                        }}
                                                    >
                                                        {speakers.map((speaker) => (
                                                            <MenuItem key={speaker} value={speaker}>
                                                                {speaker}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" sx={{
                                                color: 'rgba(255,255,255,0.9)',
                                                fontSize: '0.875rem'
                                            }}>
                                                {subtitle.text}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column' }}>
                                        {editingIndex === index ? (
                                            <>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSaveClick}
                                                    disabled={loading}
                                                    size="small"
                                                    sx={{
                                                        mb: 0.5,
                                                        minWidth: 0,
                                                        px: 1,
                                                        py: 0.5,
                                                        bgcolor: PRIMARY_COLOR,
                                                        '&:hover': {
                                                            bgcolor: SECONDARY_COLOR,
                                                        },
                                                    }}
                                                >
                                                    <SaveIcon fontSize="small" />
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleCancelClick}
                                                    disabled={loading}
                                                    size="small"
                                                    sx={{
                                                        minWidth: 0,
                                                        px: 1,
                                                        py: 0.5,
                                                        borderColor: 'rgba(255,255,255,0.23)',
                                                        color: 'rgba(255,255,255,0.7)',
                                                        '&:hover': {
                                                            borderColor: PRIMARY_COLOR,
                                                            color: PRIMARY_COLOR,
                                                        },
                                                    }}
                                                >
                                                    <CancelIcon fontSize="small" />
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Tooltip title="Edit" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleEditClick(e, index, subtitle)}
                                                        sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: PRIMARY_COLOR } }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleDeleteClick(e, index)}
                                                        sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ff4d4f' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}

                        {subtitles.length === 0 && (
                            <Box sx={{
                                p: 2,
                                textAlign: 'center',
                                color: 'rgba(255,255,255,0.5)',
                                fontSize: '0.85rem'
                            }}>
                                No subtitles available. Click "Add Subtitle" to get started.
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

export default SubtitleEditor;